'use client';
import React, { useEffect, useRef } from 'react';

interface WhyItem {
  icon: string;
  title: string;
  description: string;
}

const whyItems: WhyItem[] = [
  {
    icon: '🎯',
    title: 'Tactile = Memorable',
    description:
      'Flat-print merchandise gets forgotten. 3D-textured, raised-surface branding triggers a physical response that flat print simply cannot replicate — your brand stays top-of-mind.',
  },
  {
    icon: '📊',
    title: 'Perceived Value Multiplier',
    description:
      'Premium tactile products command 3–6x the perceived value of standard promo items. Your clients feel the quality difference before they even look at your logo.',
  },
  {
    icon: '🔄',
    title: 'Retention Engine',
    description:
      'Corporate gifting programs built on premium utility items drive measurable client retention. A $30 multi-tool with your brand on it outperforms a $300 ad spend every time.',
  },
  {
    icon: '🛡️',
    title: 'Unfair Competitive Advantage',
    description:
      'Traditional promo shops cannot replicate our 3D tactile process. When your competitors are handing out flat-print shirts, you’re deploying a brand experience they literally cannot match.',
  },
  {
    icon: '⚡',
    title: 'Built for Volume Scaling',
    description:
      'From 12-unit starter orders to 10,000-unit fleet deployments, our production infrastructure scales with your growth. No quality compromise at any volume.',
  },
  {
    icon: '🏆',
    title: 'Zero-Crack Guarantee',
    description:
      'Our flexible 3D leather patch technology is engineered to flex without cracking — even after years of daily wear. Your brand investment is protected for the long haul.',
  },
];

export default function WhyTactileSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedEls = sectionRef.current?.querySelectorAll('.animate-why');
    animatedEls?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="why-tactile"
      ref={sectionRef}
      className="py-20 px-4 relative overflow-hidden"
      style={{ backgroundColor: 'rgba(0,0,0,0)' }}
    >
      {/* Background decoration */}
      <div
        className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00ff88 0%, transparent 70%)' }}
      />

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Copy */}
          <div>
            <p className="section-label">Why Tactile Wins</p>
            <h2
              className="animate-why opacity-0 translate-y-8 transition-all duration-700 font-montserrat font-black text-white mb-6"
              style={{ fontSize: 'clamp(28px, 4vw, 48px)', letterSpacing: '-1px' }}
            >
              Most Businesses Are Using Promo{' '}
              <span style={{ color: '#00b4ff' }}>Wrong.</span>
            </h2>
            <p
              className="animate-why opacity-0 translate-y-8 transition-all duration-700 mb-6 leading-relaxed"
              style={{ color: 'rgb(136, 147, 152)', fontSize: '17px', transitionDelay: '100ms' }}
            >
              They&apos;re ordering flat-print shirts and cheap pens from the same catalog as every
              other business. That&apos;s fine. But the businesses that will{' '}
              <span className="text-white font-semibold">dominate the next decade</span> are using
              tactile merchandise as a{' '}
              <span className="font-bold" style={{ color: '#00b4ff' }}>strategic retention lever</span>{' '}
              — to build brand loyalty, command premium positioning, and make competitors irrelevant.
            </p>
            <p
              className="animate-why opacity-0 translate-y-8 transition-all duration-700 mb-8 leading-relaxed"
              style={{ color: 'rgb(136, 147, 152)', fontSize: '17px', transitionDelay: '150ms' }}
            >
              That&apos;s what Serious Swag is built for. Not hobby crafts. Not generic promo.{' '}
              <span className="font-bold text-white">Real tactile leverage. Real results.</span>
            </p>
            <a
              href="#product-suite"
              className="animate-why opacity-0 translate-y-8 transition-all duration-700 inline-flex items-center gap-2 font-montserrat font-bold uppercase text-white rounded px-8 py-4 transition-all duration-300"
              style={{
                backgroundColor: '#ff6b35',
                border: '2px solid #ff6b35',
                letterSpacing: '0.4px',
                fontSize: '15px',
                transitionDelay: '200ms',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                (e.currentTarget as HTMLElement).style.color = '#ff6b35';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#ff6b35';
                (e.currentTarget as HTMLElement).style.color = '#ffffff';
              }}
            >
              Explore the Full Suite
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M8 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>

          {/* Right: Feature Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {whyItems.map((item, index) => (
              <div
                key={item.title}
                className="animate-why opacity-0 translate-y-8 transition-all duration-700 rounded-lg p-5 border group hover:border-blue-400/40 transition-colors"
                style={{
                  borderColor: '#1a3a5c',
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  transitionDelay: `${index * 80}ms`,
                }}
              >
                <div className="text-2xl mb-3">{item.icon}</div>
                <h4
                  className="font-montserrat font-bold text-white text-sm mb-2"
                >
                  {item.title}
                </h4>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: 'rgb(136, 147, 152)' }}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
